/**
 * F-191 phase 4: the community theme, the single place of its appearance.
 *
 * The API keeps the community's themes (`/api/community-themes`) and sends the
 * active one's values in `community.appearance.theme`. This module types them,
 * turns them into CSS (palette, menu and header colors, corners, density,
 * fonts) and applies them to the page. Defaults reproduce today's look exactly:
 * an attribute or a variable is only written when the theme moves away from it.
 */
import {
  ACCENT_TEXT_VAR,
  DARK_SURFACE,
  DEFAULT_COMMUNITY_PALETTE,
  SCALE_NAMES,
  SHADE_KEYS,
  WCAG_AA_NORMAL,
  contrastRatio,
  darkScaleOf,
  foregroundVarName,
  hexToChannels,
  normalizeHex,
  paletteToCssVars,
  parseCommunityPalette,
  readableForeground,
  shadeChannelVarName,
  shadeVarName,
  type ColorScale,
  type CommunityPalette,
  type ScaleName,
  type ShadeKey,
} from './utils/color-utils'

export type ThemeMode = 'light' | 'dark' | 'auto'
export type FontKey = 'system' | 'inter' | 'manrope' | 'dm-sans' | 'plus-jakarta-sans' | 'poppins' | 'lora' | 'merriweather' | 'ibm-plex-sans'
export type RadiusKey = 'rounded' | 'soft' | 'square'
export type DensityKey = 'comfortable' | 'compact'
export type SidebarDefault = 'full' | 'rail'
/** F-191 phase 5: the side menu (today) or the bar on top. */
export type NavLayout = 'sidebar' | 'top'
/** F-191 phase 5 (§4.5.1): the closed list of native elements a theme can hide, in the API's order. */
export type HideableNative = 'people' | 'leaderboard' | 'organizedEvents' | 'languageSelector' | 'reportProblem' | 'presence'
export type ThemeSide = 'light' | 'dark'
export type SurfaceKey = 'sidebarBg' | 'sidebarText' | 'headerBg' | 'headerText' | 'browserBar'

export const SURFACE_KEYS: SurfaceKey[] = ['sidebarBg', 'sidebarText', 'headerBg', 'headerText', 'browserBar']
export const THEME_MODES: ThemeMode[] = ['light', 'dark', 'auto']
export const RADII: RadiusKey[] = ['rounded', 'soft', 'square']
export const DENSITIES: DensityKey[] = ['comfortable', 'compact']
export const SIDEBAR_DEFAULTS: SidebarDefault[] = ['full', 'rail']
export const NAV_LAYOUTS: NavLayout[] = ['sidebar', 'top']
export const HIDEABLE_NATIVE: HideableNative[] = ['people', 'leaderboard', 'organizedEvents', 'languageSelector', 'reportProblem', 'presence']

/** The navigation block of a theme (and of `community.appearance`). */
export interface ThemeNavigation {
  sidebarDefault: SidebarDefault
  layout: NavLayout
  hiddenNative: HideableNative[]
}

export const DEFAULT_NAVIGATION: ThemeNavigation = { sidebarDefault: 'full', layout: 'sidebar', hiddenNative: [] }

/** A navigation block as the API sent it (or an older one), completed with today's defaults, in the API's key order. */
export function parseNavigation(value: unknown): ThemeNavigation {
  const raw = (value && typeof value === 'object' && !Array.isArray(value) ? value : {}) as Record<string, unknown>
  const hidden = Array.isArray(raw.hiddenNative) ? raw.hiddenNative : []
  return {
    sidebarDefault: SIDEBAR_DEFAULTS.includes(raw.sidebarDefault as SidebarDefault) ? raw.sidebarDefault as SidebarDefault : 'full',
    layout: NAV_LAYOUTS.includes(raw.layout as NavLayout) ? raw.layout as NavLayout : 'sidebar',
    hiddenNative: HIDEABLE_NATIVE.filter((key) => hidden.includes(key)),
  }
}
/** Saved themes a community keeps (the ready-made ones do not count). */
export const MAX_THEMES = 10

/** One side of a theme. In `dark`, a null scale is generated from the light one. */
export interface ThemeColors {
  primary: ColorScale | null
  secondary: ColorScale | null
  sidebarBg: string | null
  sidebarText: string | null
  headerBg: string | null
  headerText: string | null
  browserBar: string | null
}

export interface ThemeValues {
  mode: ThemeMode
  light: ThemeColors & { primary: ColorScale; secondary: ColorScale }
  dark: ThemeColors
  font: { heading: FontKey; body: FontKey }
  radius: RadiusKey
  density: DensityKey
  /** Absent in the ready-made themes: using one keeps the active theme's. */
  navigation?: ThemeNavigation
}

export interface ThemeWarning { param: string; ratio: number }

export interface CommunityTheme {
  /** Null only for the virtual "Atual" (no saved theme yet); its path id is `active`. */
  id: string | null
  name: string
  /** A ready-made key, `current` (the migrated "Atual") or `image`. */
  origin: string
  active: boolean
  virtual: boolean
  values: ThemeValues
  warnings: ThemeWarning[]
  createdAt?: string | null
  updatedAt?: string | null
}

export interface ReadyMadeTheme {
  key: string
  name: string
  requiresDarkMode: boolean
  available: boolean
  values: ThemeValues
}

export interface CommunityThemeList {
  activeThemeId: string | null
  limit: number
  count: number
  darkModeAllowed: boolean
  themes: CommunityTheme[]
}

/* ------------------------------------------------------------------ */
/* Fonts                                                               */
/* ------------------------------------------------------------------ */

/**
 * The curated list (§4.4 crit. 4), served from the app's own bundle
 * (@fontsource): two weights each (400 and 600), `font-display: swap`. A font
 * is fetched only once a theme or the editor uses it.
 */
export const FONTS: Array<{ key: FontKey; label: string; stack: string; serif: boolean }> = [
  { key: 'system', label: '', stack: '', serif: false },
  { key: 'inter', label: 'Inter', stack: '"Inter", ui-sans-serif, system-ui, sans-serif', serif: false },
  { key: 'manrope', label: 'Manrope', stack: '"Manrope", ui-sans-serif, system-ui, sans-serif', serif: false },
  { key: 'dm-sans', label: 'DM Sans', stack: '"DM Sans", ui-sans-serif, system-ui, sans-serif', serif: false },
  { key: 'plus-jakarta-sans', label: 'Plus Jakarta Sans', stack: '"Plus Jakarta Sans", ui-sans-serif, system-ui, sans-serif', serif: false },
  { key: 'poppins', label: 'Poppins', stack: '"Poppins", ui-sans-serif, system-ui, sans-serif', serif: false },
  { key: 'lora', label: 'Lora', stack: '"Lora", ui-serif, Georgia, serif', serif: true },
  { key: 'merriweather', label: 'Merriweather', stack: '"Merriweather", ui-serif, Georgia, serif', serif: true },
  { key: 'ibm-plex-sans', label: 'IBM Plex Sans', stack: '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif', serif: false },
]

/** The system stack the app has always used (Tailwind's `font-sans`). */
export const SYSTEM_FONT_STACK = 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"'

const FONT_LOADERS: Record<Exclude<FontKey, 'system'>, () => Promise<unknown>> = {
  inter: () => Promise.all([import('@fontsource/inter/400.css'), import('@fontsource/inter/600.css')]),
  manrope: () => Promise.all([import('@fontsource/manrope/400.css'), import('@fontsource/manrope/600.css')]),
  'dm-sans': () => Promise.all([import('@fontsource/dm-sans/400.css'), import('@fontsource/dm-sans/600.css')]),
  'plus-jakarta-sans': () => Promise.all([import('@fontsource/plus-jakarta-sans/400.css'), import('@fontsource/plus-jakarta-sans/600.css')]),
  poppins: () => Promise.all([import('@fontsource/poppins/400.css'), import('@fontsource/poppins/600.css')]),
  lora: () => Promise.all([import('@fontsource/lora/400.css'), import('@fontsource/lora/600.css')]),
  merriweather: () => Promise.all([import('@fontsource/merriweather/400.css'), import('@fontsource/merriweather/600.css')]),
  'ibm-plex-sans': () => Promise.all([import('@fontsource/ibm-plex-sans/400.css'), import('@fontsource/ibm-plex-sans/600.css')]),
}

const loadedFonts = new Map<FontKey, Promise<unknown>>()

/** Loads a font's two weights once (the stylesheet; the files follow as text needs them). */
export function loadFont(key: FontKey): Promise<unknown> {
  if (key === 'system') return Promise.resolve()
  const cached = loadedFonts.get(key)
  if (cached) return cached
  const promise = FONT_LOADERS[key]().catch(() => undefined)
  loadedFonts.set(key, promise)
  return promise
}

export const fontStackOf = (key: FontKey): string => FONTS.find((font) => font.key === key)?.stack || SYSTEM_FONT_STACK

/* ------------------------------------------------------------------ */
/* Corners and density                                                 */
/* ------------------------------------------------------------------ */

/** Tailwind's radius steps; `rounded` is Tailwind's default (today), the others scale them down. */
const RADIUS_STEPS = ['sm', 'DEFAULT', 'md', 'lg', 'xl', '2xl', '3xl'] as const
const RADIUS_VALUES: Record<RadiusKey, string[]> = {
  rounded: ['0.125rem', '0.25rem', '0.375rem', '0.5rem', '0.75rem', '1rem', '1.5rem'],
  soft: ['1px', '2px', '3px', '4px', '6px', '8px', '12px'],
  square: ['0px', '1px', '2px', '2px', '2px', '2px', '2px'],
}
const radiusVarName = (step: (typeof RADIUS_STEPS)[number]) => (step === 'DEFAULT' ? '--radius' : `--radius-${step}`)

/** The radius variables of a corner style, for a preview block (the page uses `data-radius`). */
export function radiusVars(radius: RadiusKey): Record<string, string> {
  return Object.fromEntries(RADIUS_STEPS.map((step, i) => [radiusVarName(step), RADIUS_VALUES[radius][i]]))
}

/** The density tokens (§4.4 crit. 6), for a preview block (the page uses `data-density` from 1024 px). */
export const DENSITY_VARS: Record<DensityKey, Record<string, string>> = {
  comfortable: { '--density-row-py': '0.75rem', '--density-row-min-h': '2.75rem', '--density-card-pad': '1.5rem', '--density-field-h': '2.25rem', '--density-field-px': '0.75rem', '--density-gap': '1rem' },
  compact: { '--density-row-py': '6px', '--density-row-min-h': '36px', '--density-card-pad': '16px', '--density-field-h': '32px', '--density-field-px': '10px', '--density-gap': '12px' },
}

/* ------------------------------------------------------------------ */
/* Values                                                              */
/* ------------------------------------------------------------------ */

const emptyColors = (): ThemeColors => ({ primary: null, secondary: null, sidebarBg: null, sidebarText: null, headerBg: null, headerText: null, browserBar: null })

/** Today's look: what an older API (no `appearance.theme`) and an empty community read as. */
export function defaultThemeValues(palette: CommunityPalette | null = null, browserBar: string | null = null): ThemeValues {
  const base = palette ?? DEFAULT_COMMUNITY_PALETTE
  return {
    mode: 'light',
    light: { ...emptyColors(), primary: { ...(base.primary ?? DEFAULT_COMMUNITY_PALETTE.primary) }, secondary: { ...(base.secondary ?? DEFAULT_COMMUNITY_PALETTE.secondary) }, browserBar },
    dark: emptyColors(),
    font: { heading: 'system', body: 'system' },
    radius: 'rounded',
    density: 'comfortable',
    navigation: { ...DEFAULT_NAVIGATION, hiddenNative: [] },
  }
}

const isObject = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object' && !Array.isArray(value)
const oneOf = <T extends string>(list: readonly T[], value: unknown, fallback: T): T => (list.includes(value as T) ? (value as T) : fallback)
const hexOrNull = (value: unknown): string | null => (typeof value === 'string' ? normalizeHex(value) : null)

const scaleOf = (value: unknown): ColorScale | null => {
  if (!isObject(value)) return null
  const scale = {} as ColorScale
  for (const shade of SHADE_KEYS) {
    const hex = hexOrNull(value[shade])
    if (!hex) return null
    scale[shade] = hex
  }
  return scale
}

const colorsOf = (value: unknown, fallback: ThemeColors): ThemeColors => {
  const raw = isObject(value) ? value : {}
  const result = { ...fallback }
  for (const scale of SCALE_NAMES) result[scale] = scaleOf(raw[scale]) ?? fallback[scale]
  for (const key of SURFACE_KEYS) result[key] = raw[key] === undefined ? fallback[key] : hexOrNull(raw[key])
  return result
}

/** Theme values as the API sent them, completed with today's defaults (never throws). */
export function parseThemeValues(value: unknown, fallback: ThemeValues = defaultThemeValues()): ThemeValues {
  if (!isObject(value)) return fallback
  const light = colorsOf(value.light, fallback.light)
  const font = isObject(value.font) ? value.font : {}
  const navigation = isObject(value.navigation) ? value.navigation : null
  const fontKeys = FONTS.map((item) => item.key)
  return {
    mode: oneOf(THEME_MODES, value.mode, fallback.mode),
    light: { ...light, primary: light.primary ?? fallback.light.primary, secondary: light.secondary ?? fallback.light.secondary },
    dark: colorsOf(value.dark, emptyColors()),
    font: { heading: oneOf(fontKeys, font.heading, fallback.font.heading), body: oneOf(fontKeys, font.body, fallback.font.body) },
    radius: oneOf(RADII, value.radius, fallback.radius),
    density: oneOf(DENSITIES, value.density, fallback.density),
    ...(navigation ? { navigation: parseNavigation(navigation) } : fallback.navigation ? { navigation: fallback.navigation } : {}),
  }
}

/** The active theme of a community payload; an older API (no `appearance.theme`) reads as today's palette. */
export function communityThemeValues(community: { appearance?: unknown; themePalette?: unknown; themeColor?: string | null } | null): ThemeValues {
  const fallback = defaultThemeValues(parseCommunityPalette(community?.themePalette), community?.themeColor ? normalizeHex(community.themeColor) : null)
  const appearance = isObject(community?.appearance) ? community?.appearance as Record<string, unknown> : {}
  return parseThemeValues(appearance.theme, fallback)
}

/** The palette a side paints: the dark one generated from the light when not set. */
export function sidePalette(values: ThemeValues, side: ThemeSide): CommunityPalette {
  if (side === 'light') return { primary: values.light.primary, secondary: values.light.secondary }
  return {
    primary: values.dark.primary ?? darkScaleOf(values.light.primary),
    secondary: values.dark.secondary ?? darkScaleOf(values.light.secondary),
  }
}

/* ------------------------------------------------------------------ */
/* CSS                                                                 */
/* ------------------------------------------------------------------ */

/** The palette variables of a side (light: the phase 1 rule; dark: a custom scale paints as it is). */
export function paletteVarsOf(values: ThemeValues, side: ThemeSide): Record<string, string> {
  const light = { primary: values.light.primary, secondary: values.light.secondary }
  if (side === 'light') return paletteToCssVars(light)
  const vars = paletteToCssVars(light, 'dark')
  for (const scale of SCALE_NAMES) {
    const custom = values.dark[scale]
    if (!custom) continue
    for (const shade of SHADE_KEYS) {
      vars[shadeVarName(scale, shade)] = custom[shade]
      vars[shadeChannelVarName(scale, shade)] = hexToChannels(custom[shade]) as string
    }
    vars[foregroundVarName(scale)] = readableForeground(custom[600])
    if (scale === 'primary') {
      const shades: ShadeKey[] = [600, 500, 400, 700, 800]
      const accent = shades.find((shade) => contrastRatio(custom[shade], DARK_SURFACE) >= WCAG_AA_NORMAL)
      vars[ACCENT_TEXT_VAR] = hexToChannels(custom[accent ?? 900]) as string
    }
  }
  return vars
}

const DEFAULT_SURFACE: Record<ThemeSide, string> = { light: '#ffffff', dark: DARK_SURFACE }

/** `a` over `b` at `weight`, in sRGB. */
const mix = (a: string, b: string, weight: number): string => {
  const ca = (hexToChannels(a) ?? '0 0 0').split(' ').map(Number)
  const cb = (hexToChannels(b) ?? '0 0 0').split(' ').map(Number)
  return `#${ca.map((value, i) => Math.round(value * weight + cb[i] * (1 - weight)).toString(16).padStart(2, '0')).join('')}`
}

/** How much of the text each neutral shade carries over the region's background. */
const NEUTRAL_WEIGHTS: Record<ShadeKey, number> = { 50: 0.03, 100: 0.06, 200: 0.12, 300: 0.2, 400: 0.4, 500: 0.55, 600: 0.68, 700: 0.8, 800: 0.9, 900: 1, 950: 1 }

/**
 * The neutral tokens of a region (menu or header) with its own background
 * and text: the gray scale runs from the background to the text, so every
 * white background, gray text, border and hover inside follows. Empty when the
 * theme keeps the region on the app's surface (today).
 */
export function regionVars(background: string | null, text: string | null, side: ThemeSide): Record<string, string> {
  if (!background && !text) return {}
  const bg = background ?? DEFAULT_SURFACE[side]
  const fg = text ?? (contrastRatio(bg, '#ffffff') > contrastRatio(bg, '#111827') ? '#f9fafb' : '#111827')
  const channel = (hex: string) => hexToChannels(hex) as string
  const gray = (shade: ShadeKey) => channel(mix(fg, bg, NEUTRAL_WEIGHTS[shade]))
  const vars: Record<string, string> = { '--rgb-white': channel(bg) }
  for (const shade of SHADE_KEYS) vars[`--rgb-gray-${shade}`] = gray(shade)
  Object.assign(vars, {
    '--rgb-surface': channel(bg),
    '--rgb-surface-muted': gray(50),
    '--rgb-background': channel(bg),
    '--rgb-foreground': channel(fg),
    '--rgb-foreground-secondary': gray(600),
    '--rgb-muted': gray(100),
    '--rgb-muted-foreground': gray(500),
    '--rgb-accent': gray(100),
    '--rgb-accent-foreground': channel(fg),
    '--rgb-border': gray(200),
    '--rgb-input': gray(200),
    color: `rgb(${channel(fg)})`,
  })
  return vars
}

/** Every variable a preview block needs to look like a side of the theme. */
export function previewVars(values: ThemeValues, side: ThemeSide): Record<string, string> {
  return {
    ...paletteVarsOf(values, side),
    ...radiusVars(values.radius),
    ...DENSITY_VARS[values.density],
    '--font-body': fontStackOf(values.font.body),
    '--font-heading': fontStackOf(values.font.heading),
  }
}

/** The browser bar on mobile: the theme's, else shade 600 (what the app always used). */
export function browserBarOf(values: ThemeValues, side: ThemeSide): string {
  if (side === 'dark') return values.dark.browserBar ?? values.light.browserBar ?? sidePalette(values, 'dark').primary[600]
  return values.light.browserBar ?? values.light.primary[600]
}

/* ------------------------------------------------------------------ */
/* Applying the active theme                                           */
/* ------------------------------------------------------------------ */

const STYLE_ID = 'community-palette'

const block = (selector: string, vars: Record<string, string>) =>
  Object.keys(vars).length === 0 ? '' : `${selector} {\n${Object.entries(vars).map(([name, value]) => `  ${name}: ${value};`).join('\n')}\n}`

let applied: ThemeValues | null = null

/** The `<meta name="theme-color">` for the side on screen now. */
export function syncBrowserBar(): void {
  if (typeof document === 'undefined' || !applied) return
  const dark = document.documentElement.classList.contains('dark')
  let meta = document.querySelector('meta[name="theme-color"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('name', 'theme-color')
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', browserBarOf(applied, dark ? 'dark' : 'light'))
}

const setAttr = (name: string, value: string | null) => {
  const root = document.documentElement
  if (value === null) root.removeAttribute(name)
  else root.setAttribute(name, value)
}

/**
 * Applies the active theme to the page: the palette (light and dark), the menu
 * and header colors, corners, density and fonts. Each one only moves away from
 * index.css when the theme moves away from today's default.
 */
export function applyCommunityTheme(values: ThemeValues): void {
  if (typeof document === 'undefined') return
  applied = values
  const root = document.documentElement

  /* Inline palette properties from older builds would beat the stylesheet. */
  for (const scale of SCALE_NAMES as ScaleName[]) {
    for (const shade of SHADE_KEYS) {
      root.style.removeProperty(shadeVarName(scale, shade))
      root.style.removeProperty(shadeChannelVarName(scale, shade))
    }
    root.style.removeProperty(foregroundVarName(scale))
  }
  root.style.removeProperty(ACCENT_TEXT_VAR)

  let style = document.getElementById(STYLE_ID)
  if (!style) {
    style = document.createElement('style')
    style.id = STYLE_ID
    document.head.appendChild(style)
  }
  const regions = (['light', 'dark'] as ThemeSide[]).flatMap((side) => {
    const scope = side === 'light' ? ':root:not(.dark)' : ':root.dark'
    return [
      block(`${scope} .theme-region-sidebar`, regionVars(values[side].sidebarBg, values[side].sidebarText, side)),
      block(`${scope} .theme-region-header`, regionVars(values[side].headerBg, values[side].headerText, side)),
    ]
  })
  style.textContent = [
    block(':root,\n.theme-light', paletteVarsOf(values, 'light')),
    block(':root.dark', paletteVarsOf(values, 'dark')),
    ...regions,
  ].filter(Boolean).join('\n')

  setAttr('data-radius', values.radius === 'rounded' ? null : values.radius)
  setAttr('data-density', values.density === 'compact' ? 'compact' : null)
  for (const role of ['body', 'heading'] as const) {
    const key = values.font[role]
    if (key === 'system') {
      setAttr(`data-font-${role}`, null)
      root.style.removeProperty(`--font-${role}`)
    } else {
      void loadFont(key)
      root.style.setProperty(`--font-${role}`, fontStackOf(key))
      setAttr(`data-font-${role}`, key)
    }
  }
  syncBrowserBar()
}

/* ------------------------------------------------------------------ */
/* Contrast (the editor's warnings, §5.3; the API sends the same list)  */
/* ------------------------------------------------------------------ */

export function themeWarnings(values: ThemeValues): ThemeWarning[] {
  const warnings: ThemeWarning[] = []
  const check = (param: string, foreground: string | null, background: string | null) => {
    if (!foreground || !background) return
    const ratio = contrastRatio(foreground, background)
    if (ratio < WCAG_AA_NORMAL) warnings.push({ param, ratio: Math.round(ratio * 100) / 100 })
  }
  /* A text color over the default background (null) is checked against that default surface. */
  for (const side of ['light', 'dark'] as ThemeSide[]) {
    check(`values.${side}.sidebarText`, values[side].sidebarText, values[side].sidebarBg ?? DEFAULT_SURFACE[side])
    check(`values.${side}.headerText`, values[side].headerText, values[side].headerBg ?? DEFAULT_SURFACE[side])
  }
  for (const scale of SCALE_NAMES) {
    const solid = values.light[scale][600]
    check(`values.light.${scale}.600`, readableForeground(solid), solid)
  }
  return warnings
}

/** Two theme values are the same look. */
export const sameValues = (a: ThemeValues, b: ThemeValues): boolean => JSON.stringify(a) === JSON.stringify(b)
